import JsSIP from 'jssip';
import VoiceAPI from './voiceAPIClient';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');
const createCallDismissedEvent = (callSid, callerNumber) =>
  new CustomEvent('call:dismissed', { detail: { callSid, callerNumber } });
const createCallConnectedEvent = callSid =>
  new CustomEvent('call:connected', { detail: { callSid } });

const isCanceled = event => {
  return (
    event?.cause === JsSIP.C?.causes?.CANCELED ||
    event?.cause === 'Canceled' ||
    event?.message?.status_code === 487
  );
};

class SipVoiceClient extends EventTarget {
  constructor() {
    super();
    this.ua = null;
    this.activeConnection = null;
    this.initialized = false;
    this.inboxId = null;
    this.sessionConfig = null;
    this.pendingIncomingSessions = new Map();
    this.remoteAudio = null;
  }

  async initializeDevice(inboxId) {
    if (
      this.initialized &&
      this.inboxId === inboxId &&
      this.ua?.isRegistered()
    ) {
      return this.ua;
    }

    this.destroyDevice();

    const response = await VoiceAPI.getToken(inboxId);
    if (!response || response.provider !== 'vitalpbx') {
      throw new Error('Invalid VitalPBX session response');
    }

    this.sessionConfig = response;
    this.inboxId = inboxId;

    const {
      wss_url: wssUrl,
      sip_uri: sipUri,
      sip_username: sipUsername,
      sip_password: sipPassword,
      register_expires: registerExpires = 120,
    } = response;

    const socket = new JsSIP.WebSocketInterface(wssUrl);
    const configuration = {
      sockets: [socket],
      uri: sipUri,
      password: sipPassword,
      display_name: sipUsername,
      register: true,
      register_expires: Number(registerExpires),
      session_timers: false,
    };

    return new Promise((resolve, reject) => {
      let settled = false;
      const ua = new JsSIP.UA(configuration);
      this.ua = ua;

      ua.on('registered', () => {
        this.initialized = true;
        if (!settled) {
          settled = true;
          resolve(this.ua);
        }
      });

      ua.on('registrationFailed', e => {
        if (!settled) {
          settled = true;
          reject(new Error(e?.cause || 'SIP registration failed'));
        }
      });

      ua.on('newRTCSession', this.onNewRTCSession);

      try {
        ua.start();
      } catch (err) {
        if (!settled) {
          settled = true;
          reject(err);
        }
      }
    });
  }

  get hasActiveConnection() {
    return !!this.activeConnection;
  }

  setMuted(shouldMute) {
    if (!this.activeConnection) return false;
    if (shouldMute) {
      this.activeConnection.mute({ audio: true, video: false });
    } else {
      this.activeConnection.unmute({ audio: true, video: false });
    }
    return shouldMute;
  }

  endClientCall() {
    if (this.activeConnection) {
      try {
        this.activeConnection.terminate();
      } catch (_) {
        // noop
      }
      this.activeConnection = null;
    }

    Array.from(this.pendingIncomingSessions.values()).forEach(session => {
      try {
        session.terminate();
      } catch (_) {
        // noop
      }
    });
    this.pendingIncomingSessions.clear();
    this.stopRemoteAudio();
  }

  destroyDevice() {
    this.endClientCall();
    if (this.ua) {
      try {
        this.ua.stop();
      } catch (_) {
        // noop
      }
      this.ua = null;
    }
    this.initialized = false;
    this.inboxId = null;
    this.sessionConfig = null;
  }

  async joinClientCall({ to, conversationId, callSid, callRef }) {
    if (!this.ua || !this.initialized) {
      if (this.inboxId) {
        await this.initializeDevice(this.inboxId);
      } else {
        return null;
      }
    }

    if (this.activeConnection) {
      return this.activeConnection;
    }

    const pending = this.findPendingIncomingSession(to);
    const pcConfig = {
      iceServers: this.sessionConfig?.ice_servers || [],
    };

    if (pending) {
      const { key, session } = pending;
      this.pendingIncomingSessions.delete(key);
      session.callSid = callSid;
      this.activeConnection = session;

      this.setupSessionAudio(session);
      this.setupSessionTermination(session, callSid);
      this.setupSessionConnectionEvents(session, callSid);

      session.answer({
        mediaConstraints: { audio: true, video: false },
        pcConfig,
      });

      return session;
    }

    if (!to) return null;

    const extraHeaders = [];
    if (callRef) {
      extraHeaders.push(`X-Chatwoot-Call-Id: ${callRef}`);
    }

    const session = this.ua.call(to, {
      mediaConstraints: { audio: true, video: false },
      pcConfig,
      extraHeaders,
    });

    session.callSid = callSid;
    session.conversationId = conversationId;
    this.activeConnection = session;

    this.setupSessionAudio(session);
    this.setupSessionTermination(session, callSid);
    this.setupSessionConnectionEvents(session, callSid);

    return session;
  }

  // Signaling success (200 OK/ACK) doesn't mean audio is flowing — ICE can
  // still fail afterwards (e.g. unreachable RTP candidates, missing TURN).
  // Dispatch call:connected only once media is actually established so the UI
  // can show a "connecting" state in between.
  setupSessionConnectionEvents(session, callSid) {
    let notified = false;
    const attach = pc => {
      if (!pc?.addEventListener) return;
      pc.addEventListener('iceconnectionstatechange', () => {
        const state = pc.iceConnectionState;
        if ((state === 'connected' || state === 'completed') && !notified) {
          notified = true;
          this.dispatchEvent(createCallConnectedEvent(callSid));
        }
      });
    };
    session.on('peerconnection', ({ peerconnection }) =>
      attach(peerconnection)
    );
    if (session.connection) attach(session.connection);
  }

  onNewRTCSession = ({ originator, session, request }) => {
    if (originator !== 'remote') return;

    const callerNumber =
      session.remote_identity?.uri?.user ||
      request?.from?.uri?.user ||
      'unknown';

    this.pendingIncomingSessions.set(callerNumber, session);

    session.on('ended', event => {
      this.pendingIncomingSessions.delete(callerNumber);
      if (isCanceled(event)) {
        this.dispatchEvent(
          createCallDismissedEvent(session.callSid, callerNumber)
        );
      } else if (this.activeConnection === session) {
        this.onDisconnect();
      }
    });

    session.on('failed', event => {
      this.pendingIncomingSessions.delete(callerNumber);
      if (isCanceled(event)) {
        this.dispatchEvent(
          createCallDismissedEvent(session.callSid, callerNumber)
        );
      } else if (this.activeConnection === session) {
        this.onDisconnect();
      }
    });
  };

  findPendingIncomingSession(callerNumber) {
    if (callerNumber && this.pendingIncomingSessions.has(callerNumber)) {
      return {
        key: callerNumber,
        session: this.pendingIncomingSessions.get(callerNumber),
      };
    }

    if (callerNumber) {
      const cleanTarget = String(callerNumber).replace(/\D/g, '');
      if (cleanTarget) {
        const found = Array.from(this.pendingIncomingSessions.entries()).find(
          ([key]) => {
            const cleanKey = String(key).replace(/\D/g, '');
            return (
              cleanKey &&
              (cleanKey.endsWith(cleanTarget) || cleanTarget.endsWith(cleanKey))
            );
          }
        );
        if (found) {
          return { key: found[0], session: found[1] };
        }
      }
    }

    if (this.pendingIncomingSessions.size === 1) {
      const [key, session] = this.pendingIncomingSessions
        .entries()
        .next().value;
      return { key, session };
    }

    return null;
  }

  setupSessionTermination(session, callSid) {
    session.on('ended', event => {
      if (this.activeConnection === session) {
        this.activeConnection = null;
        this.stopRemoteAudio();
        if (isCanceled(event)) {
          this.dispatchEvent(createCallDismissedEvent(callSid));
        } else {
          this.onDisconnect();
        }
      }
    });

    session.on('failed', event => {
      if (this.activeConnection === session) {
        this.activeConnection = null;
        this.stopRemoteAudio();
        if (isCanceled(event)) {
          this.dispatchEvent(createCallDismissedEvent(callSid));
        } else {
          this.onDisconnect();
        }
      }
    });
  }

  setupSessionAudio(session) {
    if (typeof document === 'undefined') return;

    const playStream = stream => {
      if (!this.remoteAudio) {
        let el = document.getElementById('sip-remote-audio');
        if (!el) {
          el = document.createElement('audio');
          el.id = 'sip-remote-audio';
          el.autoplay = true;
          el.playsInline = true;
          el.style.display = 'none';
          document.body.appendChild(el);
        }
        this.remoteAudio = el;
      }
      this.remoteAudio.srcObject = stream;
      this.remoteAudio.play().catch(() => {});
    };

    session.on('peerconnection', ({ peerconnection }) => {
      if (peerconnection?.addEventListener) {
        peerconnection.addEventListener('track', event => {
          if (event.streams && event.streams[0]) {
            playStream(event.streams[0]);
          } else if (event.track) {
            playStream(new MediaStream([event.track]));
          }
        });
      }
    });

    if (session.connection?.addEventListener) {
      session.connection.addEventListener('track', event => {
        if (event.streams && event.streams[0]) {
          playStream(event.streams[0]);
        } else if (event.track) {
          playStream(new MediaStream([event.track]));
        }
      });
    }
  }

  stopRemoteAudio() {
    if (this.remoteAudio) {
      try {
        this.remoteAudio.pause();
        this.remoteAudio.srcObject = null;
      } catch (_) {
        // noop
      }
    }
  }

  onDisconnect = () => {
    this.activeConnection = null;
    this.stopRemoteAudio();
    this.dispatchEvent(createCallDisconnectedEvent());
  };
}

export default new SipVoiceClient();
